import {
  CancelSurveyDetailCode,
  CancelSurveyReasonCode,
  CancelSurveySubscriptionType,
} from '@griply/types';

export enum ChurnSurveyFollowUp {
  Feature = 'feature',
  Complex = 'complex',
  Issue = 'issue',
  App = 'app',
}

// Which survey is being answered. Not a wire contract — the tracked value is
// CancelSurveySubscriptionType (cancel) or nothing at all (delete-account).
export enum ChurnSurveyContext {
  SubscriptionPaid = 'subscription-paid',
  SubscriptionTrial = 'subscription-trial',
  DeleteAccount = 'delete-account',
}

export type ChurnSurveyReasonOption = {
  code: CancelSurveyReasonCode;
  labelKey: string;
};

export type ChurnSurveyDetailOption = {
  code: CancelSurveyDetailCode;
  labelKey: string;
  isOther: boolean;
};

const BASE = 'cancel-subscription-survey';
const DELETE_BASE = 'delete-account-survey';

// Only the reason step differs per survey; everything downstream (follow-up,
// details, other) branches on the reason code alone and is context-agnostic.
type ChurnSurveyContextConfig = {
  reasonCodes: CancelSurveyReasonCode[];
  reasonKeyBase: string;
};

const SURVEY_CONTEXTS: Record<ChurnSurveyContext, ChurnSurveyContextConfig> = {
  [ChurnSurveyContext.SubscriptionPaid]: {
    reasonCodes: [
      CancelSurveyReasonCode.MissingFeature,
      CancelSurveyReasonCode.NotUsing,
      CancelSurveyReasonCode.TooComplex,
      CancelSurveyReasonCode.TechnicalIssue,
      CancelSurveyReasonCode.Switched,
      CancelSurveyReasonCode.GoalReached,
      CancelSurveyReasonCode.TooExpensive,
      CancelSurveyReasonCode.Other,
    ],
    reasonKeyBase: `${BASE}.reason.paid`,
  },
  [ChurnSurveyContext.SubscriptionTrial]: {
    reasonCodes: [
      CancelSurveyReasonCode.JustTrying,
      CancelSurveyReasonCode.NoTime,
      CancelSurveyReasonCode.NotWorthYet,
      CancelSurveyReasonCode.MissingFeature,
      CancelSurveyReasonCode.TooComplex,
      CancelSurveyReasonCode.TechnicalIssue,
      CancelSurveyReasonCode.Switched,
      CancelSurveyReasonCode.Other,
    ],
    reasonKeyBase: `${BASE}.reason.trial`,
  },
  [ChurnSurveyContext.DeleteAccount]: {
    reasonCodes: [
      CancelSurveyReasonCode.NotUsing,
      CancelSurveyReasonCode.GoalReached,
      CancelSurveyReasonCode.MissingFeature,
      CancelSurveyReasonCode.TooComplex,
      CancelSurveyReasonCode.TechnicalIssue,
      CancelSurveyReasonCode.Switched,
      CancelSurveyReasonCode.Privacy,
      CancelSurveyReasonCode.DuplicateAccount,
      CancelSurveyReasonCode.Other,
    ],
    reasonKeyBase: `${DELETE_BASE}.reason`,
  },
};

const FOLLOW_UP_BY_REASON: Partial<
  Record<CancelSurveyReasonCode, ChurnSurveyFollowUp>
> = {
  [CancelSurveyReasonCode.MissingFeature]: ChurnSurveyFollowUp.Feature,
  [CancelSurveyReasonCode.TooComplex]: ChurnSurveyFollowUp.Complex,
  [CancelSurveyReasonCode.TechnicalIssue]: ChurnSurveyFollowUp.Issue,
  [CancelSurveyReasonCode.Switched]: ChurnSurveyFollowUp.App,
};

const MULTI_SELECT_FOLLOW_UPS = new Set<ChurnSurveyFollowUp>([
  ChurnSurveyFollowUp.Feature,
  ChurnSurveyFollowUp.Complex,
  ChurnSurveyFollowUp.Issue,
]);

const DETAIL_CODES_BY_FOLLOW_UP: Record<
  ChurnSurveyFollowUp,
  CancelSurveyDetailCode[]
> = {
  [ChurnSurveyFollowUp.Feature]: [
    CancelSurveyDetailCode.FeatIcal,
    CancelSurveyDetailCode.FeatProjects,
    CancelSurveyDetailCode.FeatRecurring,
    CancelSurveyDetailCode.FeatNotes,
    CancelSurveyDetailCode.FeatTimeTracking,
    CancelSurveyDetailCode.FeatOther,
  ],
  [ChurnSurveyFollowUp.Complex]: [
    CancelSurveyDetailCode.ComplexGoals,
    CancelSurveyDetailCode.ComplexHabits,
    CancelSurveyDetailCode.ComplexTasks,
    CancelSurveyDetailCode.ComplexOther,
  ],
  [ChurnSurveyFollowUp.Issue]: [
    CancelSurveyDetailCode.IssueSync,
    CancelSurveyDetailCode.IssueHabits,
    CancelSurveyDetailCode.IssuePerformance,
    CancelSurveyDetailCode.IssueOther,
  ],
  [ChurnSurveyFollowUp.App]: [
    CancelSurveyDetailCode.CompTodoist,
    CancelSurveyDetailCode.CompTickTick,
    CancelSurveyDetailCode.CompThings,
    CancelSurveyDetailCode.CompNotion,
    CancelSurveyDetailCode.CompMarvin,
    CancelSurveyDetailCode.CompSunsama,
    CancelSurveyDetailCode.CompAkiflow,
    CancelSurveyDetailCode.CompClickUp,
    CancelSurveyDetailCode.CompOther,
  ],
};

const OTHER_DETAIL_BY_FOLLOW_UP: Record<
  ChurnSurveyFollowUp,
  CancelSurveyDetailCode
> = {
  [ChurnSurveyFollowUp.Feature]: CancelSurveyDetailCode.FeatOther,
  [ChurnSurveyFollowUp.Complex]: CancelSurveyDetailCode.ComplexOther,
  [ChurnSurveyFollowUp.Issue]: CancelSurveyDetailCode.IssueOther,
  [ChurnSurveyFollowUp.App]: CancelSurveyDetailCode.CompOther,
};

const OTHER_DETAIL_CODES = new Set<CancelSurveyDetailCode>(
  Object.values(OTHER_DETAIL_BY_FOLLOW_UP),
);

export const CHURN_SURVEY_OTHER_PLACEHOLDER_KEY = `${BASE}.follow-up.other-placeholder`;
export const CHURN_SURVEY_SOMETHING_ELSE_KEY = `${BASE}.something-else`;

// Below this the answer reads as a dismissal ("no", "n/a") rather than feedback.
export const CHURN_SURVEY_MIN_OPEN_ANSWER_LENGTH = 10;

const OPEN_QUESTION_KEY = `${BASE}.open.question`;
const OPEN_PLACEHOLDER_KEY = `${BASE}.open.placeholder`;
const OPEN_PLACEHOLDER_REQUIRED_KEY = `${BASE}.open.placeholder-required`;

export const getChurnSurveyReasonOptions = (
  context: ChurnSurveyContext,
): ChurnSurveyReasonOption[] => {
  const { reasonCodes, reasonKeyBase } = SURVEY_CONTEXTS[context];
  return reasonCodes.map((code) => ({
    code,
    labelKey:
      code === CancelSurveyReasonCode.Other
        ? CHURN_SURVEY_SOMETHING_ELSE_KEY
        : `${reasonKeyBase}.options.${code}`,
  }));
};

export const getChurnSurveyReasonQuestionKey = (
  context: ChurnSurveyContext,
): string => `${SURVEY_CONTEXTS[context].reasonKeyBase}.question`;

export const getSubscriptionSurveyContext = (
  variant: CancelSurveySubscriptionType,
): ChurnSurveyContext =>
  variant === CancelSurveySubscriptionType.Trial
    ? ChurnSurveyContext.SubscriptionTrial
    : ChurnSurveyContext.SubscriptionPaid;

export const getChurnSurveyFollowUpForReason = (
  reason: CancelSurveyReasonCode,
): ChurnSurveyFollowUp | null => FOLLOW_UP_BY_REASON[reason] ?? null;

export const isChurnSurveyFollowUpMultiSelect = (
  followUp: ChurnSurveyFollowUp,
): boolean => MULTI_SELECT_FOLLOW_UPS.has(followUp);

export const getChurnSurveyFollowUpQuestionKey = (
  followUp: ChurnSurveyFollowUp,
): string => `${BASE}.follow-up.${followUp}.question`;

export const getChurnSurveyOtherDetailCode = (
  followUp: ChurnSurveyFollowUp,
): CancelSurveyDetailCode => OTHER_DETAIL_BY_FOLLOW_UP[followUp];

export const getChurnSurveyDetailOptions = (
  followUp: ChurnSurveyFollowUp,
): ChurnSurveyDetailOption[] =>
  DETAIL_CODES_BY_FOLLOW_UP[followUp].map((code) => {
    const isOther = OTHER_DETAIL_CODES.has(code);
    return {
      code,
      labelKey: isOther
        ? CHURN_SURVEY_SOMETHING_ELSE_KEY
        : `${BASE}.follow-up.options.${code}`,
      isOther,
    };
  });

export const isChurnSurveyOtherDetailCode = (
  code: CancelSurveyDetailCode,
): boolean => OTHER_DETAIL_CODES.has(code);

export const isChurnSurveyTextAnswerComplete = (text: string): boolean =>
  text.trim().length >= CHURN_SURVEY_MIN_OPEN_ANSWER_LENGTH;

// Deleting an account must never be gated on feedback.
const isSubscriptionContext = (context: ChurnSurveyContext): boolean =>
  context !== ChurnSurveyContext.DeleteAccount;

// A follow-up already forces a choice — requiring text too would stack two mandatory steps.
export const isChurnSurveyOpenAnswerRequired = (
  context: ChurnSurveyContext,
  reason: CancelSurveyReasonCode | null,
): boolean =>
  isSubscriptionContext(context) &&
  !!reason &&
  !getChurnSurveyFollowUpForReason(reason);

export const getChurnSurveyOpenQuestionKey = (
  context: ChurnSurveyContext,
  reason: CancelSurveyReasonCode | null,
): string =>
  isChurnSurveyOpenAnswerRequired(context, reason) && reason
    ? `${BASE}.open.questions.${reason}`
    : OPEN_QUESTION_KEY;

export const getChurnSurveyOpenPlaceholderKey = (
  context: ChurnSurveyContext,
  reason: CancelSurveyReasonCode | null,
): string =>
  isChurnSurveyOpenAnswerRequired(context, reason)
    ? OPEN_PLACEHOLDER_REQUIRED_KEY
    : OPEN_PLACEHOLDER_KEY;

export const isChurnSurveyDetailTextRequired = (
  context: ChurnSurveyContext,
): boolean => isSubscriptionContext(context);
